// frontend/src/contexts/ThemeContext.tsx
import React, { createContext, useContext, useState, useEffect } from 'react';

type Theme = 'dark' | 'light';

interface ThemeContextType {
	theme: Theme;
	toggleTheme: () => void;
	isDark: boolean;
}

const ThemeContext = createContext<ThemeContextType>({
	theme: 'dark',
	toggleTheme: () => { },
	isDark: true,
});

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const [theme, setTheme] = useState<Theme>(() => {
		return (localStorage.getItem('miniquest_theme') as Theme) || 'dark';
	});

	useEffect(() => {
		localStorage.setItem('miniquest_theme', theme);
		document.documentElement.setAttribute('data-theme', theme);
	}, [theme]);

	const toggleTheme = () => setTheme(t => t === 'dark' ? 'light' : 'dark');

	return (
		<ThemeContext.Provider value={{ theme, toggleTheme, isDark: theme === 'dark' }}>
			{children}
		</ThemeContext.Provider>
	);
};

export const useTheme = () => useContext(ThemeContext);

// Theme token helper - use this everywhere instead of hardcoded colors
export const t = (isDark: boolean) => ({
	// Backgrounds - coastal/earthy identity: deep ocean-night in dark mode
	// (teal through a warm charcoal-amber horizon), sandy coastal morning in
	// light mode - replacing the purple/indigo gradient that had become the
	// generic "AI app" signature.
	pageBg: isDark
		? 'linear-gradient(135deg, #071e26 0%, #0b3d4d 50%, #2b1f1a 100%)'
		: 'linear-gradient(135deg, #fff7ed 0%, #e0f2fe 50%, #ffe4d6 100%)',
	cardBg: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.75)',
	cardBorder: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
	navBg: isDark ? 'rgba(7,30,38,0.85)' : 'rgba(255,255,255,0.8)',
	navBorder: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
	inputBg: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(255,255,255,0.9)',
	inputBorder: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.15)',
	sidebarBg: isDark ? 'rgba(7,30,38,0.97)' : 'rgba(255,255,255,0.97)',
	sidebarBorder: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
	progressCardBg: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.85)',
	progressCardBorder: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
	progressTrackBg: isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0',
	stepCardBg: isDark ? 'rgba(20,184,166,0.1)' : '#f0fdfa',
	stepCardBorder: isDark ? 'rgba(20,184,166,0.3)' : '#99f6e4',
	stepCardIdleBg: isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc',
	stepCardIdleBorder: isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0',
	historyItemBorder: isDark ? 'rgba(255,255,255,0.05)' : '#f1f5f9',
	convItemBg: (active: boolean) => active
		? (isDark ? 'rgba(20,184,166,0.2)' : '#ccfbf1')
		: (isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc'),
	convItemBorder: (active: boolean) => active
		? (isDark ? 'rgba(94,234,212,0.4)' : '#0d9488')
		: (isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0'),
	researchBg: isDark ? 'rgba(16,185,129,0.08)' : '#f0fdf4',
	researchBorder: isDark ? 'rgba(16,185,129,0.25)' : '#bbf7d0',
	loadingBg: isDark ? 'rgba(20,184,166,0.08)' : '#f0fdfa',
	loadingBorder: isDark ? 'rgba(20,184,166,0.2)' : '#99f6e4',
	formTextareaBg: isDark ? 'rgba(255,255,255,0.07)' : 'white',
	formTextareaBorder: isDark ? 'rgba(255,255,255,0.15)' : '#d1d5db',
	// Blobs - teal + warm coral instead of purple + blue
	blob1: isDark ? 'rgba(20,184,166,0.28)' : 'rgba(20,184,166,0.14)',
	blob2: isDark ? 'rgba(249,115,22,0.2)' : 'rgba(249,115,22,0.12)',
	// Text
	textPrimary: isDark ? 'white' : '#1e293b',
	textSecondary: isDark ? 'rgba(255,255,255,0.65)' : '#64748b',
	// Light-mode value darkened from slate-400 (#94a3b8, ~2.5:1 on white - fails
	// WCAG AA) to slate-500 territory (~4.6:1) for legible small print.
	textMuted: isDark ? 'rgba(255,255,255,0.35)' : '#6b7280',
	textAccent: isDark ? '#5eead4' : '#0d9488',
	// Success/error/warning are left as the conventional green/red/amber -
	// familiarity matters more than branding for status colors users scan
	// for instantly.
	textGreen: isDark ? '#6ee7b7' : '#15803d',
	get researchTitle() { return this.textGreen; },
	success: '#10b981',
	successDark: '#059669',
	error: '#ef4444',
	errorDark: '#dc2626',
	warning: '#f59e0b',
	warningDark: '#d97706',
	// Named gradients. gradientPrimary is the main CTA gradient - now a warm
	// sunset (orange -> coral-red) for energy/action instead of the generic
	// purple-blue "AI app" gradient. gradientAccent carries cooler
	// trust/navigation moments (teal -> cyan) as the secondary brand tone.
	gradientPrimary: 'linear-gradient(135deg, #f2994a 0%, #eb5757 100%)',
	gradientAccent: 'linear-gradient(135deg, #0d9488 0%, #0891b2 100%)',
	gradientSuccess: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
	gradientWarning: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
	// Links
	linkColor: isDark ? '#5eead4' : '#0d9488',
	// Active nav
	activeNavBg: isDark ? 'rgba(20,184,166,0.2)' : 'rgba(20,184,166,0.12)',
	activeNavBorder: isDark ? 'rgba(94,234,212,0.35)' : 'rgba(13,148,136,0.3)',
	activeNavText: isDark ? '#5eead4' : '#0d9488',
	inactiveNavText: isDark ? 'rgba(255,255,255,0.65)' : '#64748b',
	// Buttons
	logoutBg: isDark ? 'rgba(239,68,68,0.15)' : 'rgba(239,68,68,0.08)',
	logoutBorder: isDark ? 'rgba(239,68,68,0.25)' : 'rgba(239,68,68,0.2)',
	logoutText: isDark ? '#fca5a5' : '#dc2626',
	secondaryBtnBg: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
	secondaryBtnText: isDark ? 'rgba(255,255,255,0.8)' : '#475569',
	secondaryBtnBorder: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)',
	userInfoBg: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
	userInfoBorder: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
});